import { Box, Stack, Typography } from "@mui/material";
import { ShotDistributionData } from "./types";
import { formatRank } from "./utils";
import { getColorTS } from "../utils";
import { grey } from "@mui/material/colors";

const shotTypes = [
  { rateKey: "RimRate", tsKey: "RimTS%" },
  { rateKey: "MidRate", tsKey: "MidTS%" },
  { rateKey: "3Rate", tsKey: "3TS%" },
] as const;

interface ShotDistributionBarProps {
  data: ShotDistributionData;
  width?: number;
  context?: "offense" | "defense";
}

export default function ShotDistributionBar({
  data,
  width,
  context,
}: ShotDistributionBarProps) {
  const hasData = shotTypes.some(({ rateKey }) => data[rateKey]?.value !== 0);

  if (!hasData) {
    return <Typography color="text.disabled">—</Typography>;
  }

  return (
    <Stack
      sx={{
        width: width ?? "100%",
      }}
      gap="1px"
    >
      <Stack
        direction="row"
        gap={"1px"}
        sx={{
          height: 10,
          borderRadius: 0,
          overflow: "hidden",
        }}
      >
        {shotTypes.map(({ rateKey, tsKey }, index) => (
          <Box
            key={index}
            sx={{
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: grey[400],
              boxSizing: "border-box",
              width: `${data[rateKey].value || 0}%`,
              bgcolor: getColorTS(data[tsKey].value, context),
            }}
          />
        ))}
      </Stack>

      <Stack
        direction="row"
        gap={"1px"}
        sx={{
          borderRadius: 0.5,
          overflow: "hidden",
        }}
      >
        {shotTypes.map(({ rateKey, tsKey }, index) => {
          const { rank, value } = data[rateKey];
          const formattedRank = formatRank(rank);

          // Determine label suffix based on contex
          // Offense: most | least | most (RIM | MID | 3P)
          // Defense: least | most | least (RIM | MID | 3P)
          const getLabelSuffix = () => {
            if (context === "offense") {
              return index === 1 ? "least" : "most";
            } else {
              return index === 1 ? "most" : "least";
            }
          };

          const displayValue =
            typeof value === "number" ? value.toFixed(0) : value;
          const rankLabel = formattedRank
            ? `${formattedRank} ${getLabelSuffix()}`
            : value
              ? `${displayValue}%\nof shots`
              : null;
          return (
            <Box
              key={rateKey}
              sx={{
                flex: 1,
              }}
            >
              <Typography
                key={rateKey}
                variant="caption"
                component="div"
                lineHeight={1.1}
                sx={{
                  textAlign:
                    index === 0 ? "left" : index === 1 ? "center" : "right",
                  whiteSpace: "nowrap",
                  fontSize: "0.525rem",
                }}
              >
                <Typography
                  variant="inherit"
                  component="span"
                  sx={{
                    bgcolor: "white",
                  }}
                >
                  {rankLabel}
                </Typography>
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
}
